import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useApi } from "../api/ApiContext";
import { ApiError, totpReuseHint } from "../api/errors";
import { Banner, Button, Dialog, Input } from "../components/ui";
import { getAssertion, WebAuthnCancelledError, webAuthnSupported } from "../lib/webauthn";
import { useOptionalAuth } from "./AuthProvider";
import { RateLimitNotice, useRateLimit } from "./rateLimit";

// Step-up MFA interceptor UI (UX-DESIGN.md §4.1: "a financial action ...
// requires a fresh, recent step-up authentication ... preferably
// phishing-resistant"). The API client calls the registered handler when
// a request comes back 403 mfa_step_up_required (RequireRecentMFA, 15-min
// freshness — internal/auth/oauth.go stepUpFreshness); this dialog
// verifies with a security key (POST /v1/auth/mfa/webauthn/step-up/*) or
// an authenticator code (POST /v1/auth/mfa/step-up), and the client then
// retries the original request once, with the same idempotency key.

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { client, endpoints } = useApi();
  const [open, setOpen] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  // Absent when the dialog is rendered on its own (tests); in the app it
  // re-reads /v1/me after a step-up so mfa_fresh_until is current.
  const auth = useOptionalAuth();
  const refreshMe = auth?.refreshMe;
  const [busy, setBusy] = useState<"totp" | "key" | null>(null);
  // Step-up is rate limited like login (429 + Retry-After).
  const rl = useRateLimit();

  useEffect(() => {
    client.setStepUpHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolver.current = resolve;
          setCode("");
          setError(null);
          setHint(null);
          setOpen(true);
        }),
    );
    return () => client.setStepUpHandler(null);
  }, [client]);

  const finish = useCallback(
    (ok: boolean) => {
      setOpen(false);
      setBusy(null);
      const r = resolver.current;
      resolver.current = null;
      r?.(ok);
      if (ok) void refreshMe?.();
    },
    [refreshMe],
  );

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    const trimmed = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter the 6-digit code from your authenticator app.");
      setHint(null);
      return;
    }
    setBusy("totp");
    setError(null);
    try {
      await endpoints.auth.stepUpTotp(trimmed);
      finish(true);
    } catch (err) {
      setBusy(null);
      if (rl.capture(err)) {
        setError(null);
        setHint(null);
      } else {
        setError(err instanceof ApiError ? err.message : "That didn't work. Try again.");
        // After signing in with a code, the step-up often gets the same one.
        setHint(totpReuseHint(err));
      }
    }
  }

  async function useKey() {
    setBusy("key");
    setError(null);
    setHint(null);
    try {
      const { options } = await endpoints.auth.webauthn.stepUpBegin();
      const credential = await getAssertion(options);
      await endpoints.auth.webauthn.stepUpFinish(credential);
      finish(true);
    } catch (err) {
      setBusy(null);
      if (rl.capture(err)) setError(null);
      else if (err instanceof WebAuthnCancelledError) setError(err.message);
      else if (err instanceof ApiError && err.code === "auth: mfa not enrolled")
        setError("No security key is registered on this account. Use your authenticator app code.");
      else setError(err instanceof Error ? err.message : "That didn't work. Try again.");
    }
  }

  return (
    <>
      {children}
      <Dialog
        open={open}
        onClose={() => finish(false)}
        dismissible={busy === null}
        title="Confirm it's you"
        description="This action moves money or changes where it goes, so we need a fresh verification (valid for 15 minutes)."
        actions={
          <Button variant="ghost" onClick={() => finish(false)} disabled={busy !== null}>
            Cancel
          </Button>
        }
      >
        <div className="stack">
          {error ? (
            <Banner tone="error" live="alert">
              <div className="stack-sm">
                <span>{error}</span>
                {hint ? <span className="small">{hint}</span> : null}
              </div>
            </Banner>
          ) : null}
          <RateLimitNotice state={rl} />
          {webAuthnSupported() ? (
            <Button variant="primary" onClick={useKey} loading={busy === "key"} disabled={busy === "totp" || rl.limited}>
              Use security key or passkey
            </Button>
          ) : null}
          <form className="stack-sm" onSubmit={submitCode} noValidate>
            <Input
              label="Authenticator app code"
              inputMode="numeric"
              autoComplete="one-time-code"
              mono
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus={!webAuthnSupported()}
            />
            <Button type="submit" variant="secondary" loading={busy === "totp"} disabled={busy === "key" || rl.limited}>
              Verify code
            </Button>
          </form>
          <p className="xs muted">Recovery codes can't be used here — they're only for signing in when you've lost your device.</p>
        </div>
      </Dialog>
    </>
  );
}
